// MentalWellnessScreening.jsx
// Production-ready Mental Wellness Screening
// Uses: ScreeningLayout.jsx (shared shell) + mentalWellnessScreeningData.js (JSON config)
import React, { useRef, useState } from 'react';
import {
  AppShell, Card, CardHeader, CardBody, CardFooter,
  ProgressBar, Sidebar,
  Logo, ChevronRight, Check, ReviewRow,
  ToggleGroup, ConcernGrid,
  ResultGauge, ConfidenceBar, NextActionList,
  CrisisAlert,
} from './ScreeningLayout';
import styles from './screening.module.css';
import {
  MENTAL_STEPS, MENTAL_FLOW,
  MENTAL_INTRO, MENTAL_CONSENT, MENTAL_CONCERNS,
  MENTAL_QUESTION_SCREENS, MENTAL_REVIEW_SECTIONS,
  computeMentalScore, getMentalRisk,
} from './mentalWellnessScreeningData';
// import { ConsentScreen } from '../../pages/CareNavigator';
import PregnancyWelcomeWeb from '../PregnancyRiskScreening/PregWelcomeScreen';
import ConsentScreen from '../Reusablecomponents/Consentscreen/Consentscreen';
// import ConsentScreen from '../CareJourney/Consentscreen';
export const RISK_SCREENING_CONSENT = {
  "pageTitle": "Consent & Privacy",
  "pageSubtitle": "Before we personalize your risk screening, please review how your information will be used.",
  "illustrationEmoji": "🛡️",
  "requiredItems": [
    { "id": "carePersonalization", "icon": "🤝", "title": "Risk Screening Personalization", "description": "Use my answers to personalize my risk screening.", "type": "checkbox", "required": true },
    { "id": "clinicAccess", "icon": "👥", "title": "Clinic Care Team Access", "description": "Allow the selected clinic's authorized risk screening team to view my submitted information.", "type": "checkbox", "required": true },
    { "id": "privacyNotice", "icon": "📋", "title": "Privacy Notice Acknowledgement", "description": "I have read and understand the privacy notice.", "type": "checkbox", "required": true }
  ],

  "sideCard": {
    "title": "Your privacy is our priority",
    "description": "We collect only what's necessary to provide safe, effective, and personalized care for you.",
    "items": [
      { "icon": "🛡️", "title": "Secure & Encrypted", "desc": "Your data is encrypted and stored securely." },
      { "icon": "🔒", "title": "Private by Design", "desc": "We limit access to your data to authorized risk screening team members only." },
      { "icon": "👤", "title": "You're in Control", "desc": "You can manage or withdraw optional consent anytime." }
    ],
    "infoBubble": "You can manage optional consent later from your profile.",
    "ctaLabel": "Continue Securely"
  }
};
// ── Illustrations ────────────────────────────────────────────────────────────
function MentalIllustration() {
  return (
    <svg viewBox="0 0 260 280" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.illusSvg}>
      <circle cx="145" cy="130" r="105" fill="#CCFBF1" opacity=".45" />
      {/* Brain illustration */}
      <ellipse cx="130" cy="115" rx="52" ry="44" fill="#5EEAD4" opacity=".25" />
      <path d="M82 115C82 90 104 72 130 72C156 72 178 90 178 115C178 136 162 152 140 158L130 162L120 158C98 152 82 136 82 115Z" fill="#0D9488" opacity=".75" />
      {/* Left lobe detail */}
      <path d="M82 115Q78 100 85 92Q92 84 100 88" stroke="#0F766E" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M88 130Q80 124 80 114" stroke="#0F766E" strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* Right lobe detail */}
      <path d="M178 115Q182 100 175 92Q168 84 160 88" stroke="#0F766E" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M172 130Q180 124 180 114" stroke="#0F766E" strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* Centre crease */}
      <path d="M130 72L130 162" stroke="#0F766E" strokeWidth="1.5" strokeDasharray="4 3" fill="none" />
      {/* Neural dots */}
      {[[100, 105], [118, 95], [142, 95], [160, 105], [108, 125], [152, 125], [130, 112]].map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r="3.5" fill="#99F6E4" />
      ))}
      {/* Person figure */}
      <circle cx="130" cy="205" r="20" fill="#99F6E4" />
      <path d="M118 200Q122 188 136 188Q148 192 145 206" fill="#0D9488" opacity=".9" />
      <path d="M108 245Q112 222 130 220Q148 222 152 245" fill="#0F766E" opacity=".85" />
      <path d="M112 228Q96 236 92 246" stroke="#99F6E4" strokeWidth="5" strokeLinecap="round" fill="none" />
      <path d="M148 228Q164 236 168 246" stroke="#99F6E4" strokeWidth="5" strokeLinecap="round" fill="none" />
      {/* Smile */}
      <path d="M122 207Q130 214 138 207" stroke="#0D9488" strokeWidth="2" strokeLinecap="round" fill="none" />
      {/* Stars / sparkles */}
      <path d="M68 80L70 74L72 80L78 82L72 84L70 90L68 84L62 82Z" fill="#5EEAD4" opacity=".8" />
      <path d="M185 70L186 66L187 70L191 71L187 72L186 76L185 72L181 71Z" fill="#99F6E4" opacity=".7" />
      <path d="M72 152L73 149L74 152L77 153L74 154L73 157L72 154L69 153Z" fill="#5EEAD4" opacity=".6" />
    </svg>
  );
}

function ConsentIllustration() {
  return (
    <svg viewBox="0 0 80 80" fill="none" className={styles.consentIllusSvg}>
      <rect x="15" y="10" width="50" height="60" rx="6" fill="#CCFBF1" stroke="#0D9488" strokeWidth="1.5" />
      <rect x="22" y="22" width="36" height="4" rx="2" fill="#0D9488" opacity=".4" />
      <rect x="22" y="32" width="28" height="4" rx="2" fill="#0D9488" opacity=".3" />
      <rect x="22" y="42" width="32" height="4" rx="2" fill="#0D9488" opacity=".3" />
      <circle cx="58" cy="58" r="14" fill="#0D9488" />
      <path d="M52 58L56 62L64 54" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SUB-SCREENS
// ══════════════════════════════════════════════════════════════════════════════

function IntroScreen({ config, onStart }) {
  return (
    <CardBody>
      <div className={styles.introWrap}>
        <div className={styles.introContent}>
          <span className={styles.introBadge}>
            <span>{config.badgeIcon}</span> {config.badge}
          </span>
          <h1 className={styles.introHeading}>{config.heading}</h1>
          <p className={styles.introDesc}>{config.desc}</p>
          <ul className={styles.introBullets}>
            {config.bullets.map((b) => (
              <li key={b.text} className={styles.introBullet}>
                <span className={styles.introBulletIcon}>{b.icon}</span>
                <span>{b.text}</span>
              </li>
            ))}
          </ul>
          <div className={styles.introPrivacy}>🛡️ {config.privacy}</div>
          <button className={styles.introStartBtn} onClick={onStart}>
            {config.startLabel} <ChevronRight />
          </button>
          <button className={styles.introLaterBtn}>{config.laterLabel}</button>
        </div>
        <div className={styles.introIllus}>
          <div className={styles.illusBlob} />
          <MentalIllustration />
        </div>
      </div>
    </CardBody>
  );
}

// function ConsentScreen({ config, onAgree, onBack }) {
//   const [checked, setChecked] = useState(false);
//   return (
//     <CardBody>
//       <div className={styles.consentWrap}>
//         <div className={styles.consentIllusWrap}><ConsentIllustration /></div>
//         <h2 className={styles.consentHeading}>{config.heading}</h2>
//         {config.paragraphs.map((p, i) => (
//           <p key={i} className={styles.consentText}>{p}</p>
//         ))}
//         <CrisisAlert>
//           If you are in immediate danger, please call emergency services (112) or go to your nearest A&E now.
//         </CrisisAlert>
//         <button
//           className={`${styles.consentCheckBtn} ${checked ? styles.consentCheckBtnActive : ''}`}
//           onClick={() => setChecked((v) => !v)}
//         >
//           <span className={styles.consentBox}>{checked && <Check />}</span>
//           <span>{config.checkLabel}</span>
//         </button>
//         <button className={styles.consentAgreeBtn} disabled={!checked} onClick={onAgree}>
//           {config.agreeLabel} <ChevronRight />
//         </button>
//       </div>
//     </CardBody>
//   );
// }

function ConcernsScreen({ config, selected, onChange, onNext, onBack }) {
  return (
    <>
      <CardBody>
        <div className={styles.screenStepBadge}>
          <span className={styles.screenStepNum}>1</span> Main Concerns
        </div>
        <h2 className={styles.screenTitle}>What are you experiencing?</h2>
        <p className={styles.screenSubtitle}>Select all that apply</p>
        <ConcernGrid options={config} selected={selected} onChange={onChange} />
      </CardBody>
      <CardFooter onBack={onBack} onNext={onNext} nextLabel="Continue" disabled={selected.length === 0} />
    </>
  );
}

function QuestionScreen({ screenConfig, answers, onChange, onNext, onBack, questionIndex, totalQuestions,qIdxMap }) {
  const screenId = screenConfig.id;

  // const [qIdx, setQIdx] = useState(0);
    const [qIdx, setQIdx] = useState(
      () => qIdxMap?.current?.[screenId] ?? 0
    );
 const updateQIdx = (newIdx) => {
    setQIdx(newIdx);
    if (qIdxMap?.current) qIdxMap.current[screenId] = newIdx;
  };
  const visible = screenConfig.questions.filter((q) => {
    if (!q.conditional) return true;
    const { field, notValue, value } = q.conditional;
    const fv = answers[field];
    if (value !== undefined) return fv === value;
    if (notValue !== undefined) return fv !== notValue;
    return true;
  });

  const current = visible[qIdx];
  const isLast = qIdx === visible.length - 1;
  const isRedFlag = current?.redFlag && answers[current?.id] && answers[current?.id] !== 'No, not at all';

  // const handleNext = () => { if (!isLast) { setQIdx((i) => i + 1); return; } onNext(); };
  // const handleBack = () => { if (qIdx > 0) { setQIdx((i) => i - 1); return; } onBack(); };

    const handleNext = () => { if (!isLast) { updateQIdx(qIdx + 1); return; } onNext(); };
  const handleBack = () => { if (qIdx > 0) { updateQIdx(qIdx - 1); return; } onBack(); };

  return (
    <>
      <ProgressBar
        // step={questionIndex + 1}
        // total={totalQuestions}
        step={qIdx}
        total={visible.length}
        label={screenConfig.stepLabel}
      />
      <CardBody>
        <div className={styles.screenStepBadge}>
          <span className={styles.screenStepNum}>{screenConfig.icon}</span>
          {screenConfig.stepLabel}
        </div>
        <div className={styles.questionCounter}>
          <span className={styles.questionCountCurrent}>Question: {qIdx + 1}</span>
          <span className={styles.questionCountSep}>/</span>
          <span className={styles.questionCountTotal}>{visible.length}</span>
        </div>
        {/* <h2 className={styles.screenTitle}>{screenConfig.title}</h2> */}
        {qIdx === 0 && <p className={styles.screenSubtitle}>{screenConfig.subtitle}</p>}

        {current && (
          <div className={styles.questionBlock}>
            <label className={styles.questionLabel}>{current.label}</label>
            <ToggleGroup
              options={current.options}
              value={answers[current.id]}
              onChange={(v) => onChange(current.id, v)}
            />
            {isRedFlag && (
              <CrisisAlert>
                We are concerned about you. Please reach out to a crisis line (iCall: 9152987821) or go to your nearest emergency department. You do not have to face this alone.
              </CrisisAlert>
            )}
          </div>
        )}
      </CardBody>
      <CardFooter
        onBack={handleBack}
        onNext={handleNext}
        nextLabel={isLast ? 'Continue' : 'Next'}
        disabled={!answers[current?.id]}
      />
    </>
  );
}

function ReviewScreen({ answers, sections, onEdit, onNext, onBack }) {
  return (
    <div  style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* <CardBody> */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 24px" }} >
      <div className={styles.screenStepBadge}>
        <span className={styles.screenStepNum}>📋</span> Review
      </div>
      <h2 className={styles.screenTitle}>Review your answers</h2>
      <p className={styles.screenSubtitle}>You can edit your responses before submitting</p>
      <div className={styles.reviewList}>
        {sections.map((sec) => {
          const vals = sec.fields.map((f) => answers[f]).filter(Boolean);
          return (
            <ReviewRow
              key={sec.id}
              icon={sec.icon}
              label={sec.label}
              value={vals.length ? vals.join(' · ') : '—'}
              onEdit={() => onEdit(sec.id)}
            />
          );
        })}
      </div>
      </div>
      {/* </CardBody> */}
      <CardFooter onBack={onBack} onNext={onNext} nextLabel="Show my result" />
    </div>
  );
}

function ResultScreen({ answers, onRestart }) {
  const { pct, crisis } = computeMentalScore(answers);
  const risk = getMentalRisk(pct, crisis);
  const conf = crisis ? 98 : Math.min(72 + Math.round(pct * 0.12), 93);

  return (
    <div className={styles.cardBodyFull}>
      <h2 className={styles.screenTitle}>Your Wellness Check Result</h2>
      <p className={styles.resultDisclaimer}>This is not a clinical diagnosis.</p>

      {crisis && (
        <CrisisAlert>
          If you are in immediate danger, please call emergency services (112) or iCall: 9152987821 right now.
        </CrisisAlert>
      )}

      <ResultGauge
        pct={crisis ? 100 : pct}
        riskLevel={risk.level}
        riskColor={risk.color}
        riskBg={risk.bg}
        riskBorder={risk.border}
        riskEmoji={risk.emoji}
        riskDesc={risk.desc}
      />
      {!crisis && <ConfidenceBar pct={conf} color={risk.color} />}

      <div className={styles.resultMeansCard}>
        <p className={styles.resultSectionTitle}>What this means</p>
        <p className={styles.resultMeansText}>{risk.means}</p>
      </div>

      <p className={styles.resultSectionTitle}>Support options for you</p>
      <NextActionList actions={risk.actions} />

      <button className={styles.resultPrimaryBtn} onClick={onRestart}>
        Start a new check-in <ChevronRight />
      </button>
      <button className={styles.resultSecondaryBtn}>Back to dashboard</button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ORCHESTRATOR
// ══════════════════════════════════════════════════════════════════════════════
const QUESTION_SCREEN_IDS = MENTAL_QUESTION_SCREENS.map((s) => s.id);

export default function MentalWellnessScreening() {
  const [flowIndex, setFlowIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [concerns, setConcerns] = useState([]);
const qIdxMapRef = useRef({});

  const currentId = MENTAL_FLOW[flowIndex];
  // const goNext = () => setFlowIndex((i) => Math.min(i + 1, MENTAL_FLOW.length - 1));
    const goNext = () => {
    setFlowIndex((i) => {
      const nextIndex = Math.min(i + 1, MENTAL_FLOW.length - 1);
      const nextId = MENTAL_FLOW[nextIndex];
      // Reset the next screen's question index so it always starts from Q1
      if (qIdxMapRef.current[nextId] !== undefined) {
        qIdxMapRef.current[nextId] = 0;
      }
      return nextIndex;
    });
  };
  const goBack = () => setFlowIndex((i) => Math.max(i - 1, 0));

  const handleAnswer = (id, val) => setAnswers((prev) => ({ ...prev, [id]: val }));
  const handleRestart = () => { setFlowIndex(0); setAnswers({}); setConcerns([]); };
  // const handleEdit = (sectionId) => {
  //   const idx = MENTAL_FLOW.indexOf(sectionId);
  //   if (idx !== -1) setFlowIndex(idx);
  // };

  const handleEdit = (sectionId) => {
  const idx = MENTAL_FLOW.indexOf(sectionId);
  if (idx !== -1) {
    qIdxMapRef.current[sectionId] = 0; // ← reset to first question
    setFlowIndex(idx);
  }
};
  const sidebarIndex = MENTAL_STEPS.findIndex((s) => s.id === currentId);
  const qScreenIdx = QUESTION_SCREEN_IDS.indexOf(currentId);
  const isQuestion = qScreenIdx !== -1;
  const showSidebar = !['intro', 'consent', 'result'].includes(currentId);
  const content = {
    "title": "Risk Screening Personalization",
    "description": "Use my answers to personalize my health risk screening.",
    "pageSubtitle": "Let's find or create your profile so we can personalize your risk screening.",
  }

  return (
    <AppShell accent="accentTeal">
      <Card>
        {currentId !== 'intro'
          ? <CardHeader
            stepLabel={currentId !== 'result' ? MENTAL_STEPS.find((s) => s.id === currentId)?.label : null}
            onExit={currentId !== 'result' ? handleRestart : undefined}
          />
          : <div style={{ padding: '18px 24px 0' }}><Logo /></div>
        }

        {/* {currentId === 'intro' && <IntroScreen config={MENTAL_INTRO} onStart={goNext} />} */}
        {currentId === 'intro' &&
          <PregnancyWelcomeWeb
            onNext={goNext}
            eyebrow="Mental Wellness"
            headlineAccent="mental wellbeing"
            bodyText="This brief screening helps us understand how you're feeling emotionally so we can support you in the right way."
          // illustration={<MentalWellnessSVG />}
          // onNext={handleNext}
          />}
        {/* {currentId === 'consent' &&
          <ConsentScreen
            config={MENTAL_CONSENT}
            onNext={goNext} onBack={goBack}
            content={content}
          />} */}
        {currentId === 'consent' &&
          <div className={styles.consentLayout}>

            <ConsentScreen
              config={RISK_SCREENING_CONSENT}
              // styles={cardstyles}
              onNext={goNext}
              onBack={goBack}
              content={content}
            />
          </div>

        }
        {currentId === 'concerns' && (
          <ConcernsScreen
            config={MENTAL_CONCERNS}
            selected={concerns}
            onChange={setConcerns}
            onNext={goNext}
            onBack={goBack}
          />
        )}
        {isQuestion && (
          <div className={styles.cardSep}>
            <div className={styles.cardSepLeft}>
              <QuestionScreen
                key={currentId}
                screenConfig={MENTAL_QUESTION_SCREENS[qScreenIdx]}
                answers={answers}
                onChange={handleAnswer}
                onNext={goNext}
                onBack={goBack}
                questionIndex={qScreenIdx}
                totalQuestions={QUESTION_SCREEN_IDS.length}
                qIdxMap={qIdxMapRef}
              />
            </div>
            {showSidebar && <Sidebar steps={MENTAL_STEPS} currentStep={sidebarIndex} />}
          </div>

        )}
        {currentId === 'review' && (
          <ReviewScreen
            answers={answers}
            sections={MENTAL_REVIEW_SECTIONS}
            onEdit={handleEdit}
            onNext={goNext}
            onBack={goBack}
          />
        )}
        {currentId === 'result' && (
          <ResultScreen answers={answers} onRestart={handleRestart} />
        )}
      </Card>


    </AppShell>
  );
}
